import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import * as Checkbox from '@radix-ui/react-checkbox';
import { type ReactNodeViewProps, NodeViewContent } from '@tiptap/react';
import { CheckIcon, XIcon } from 'lucide-react';
import { memo, useCallback } from 'react';

const TestQuestionChoiceNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, editor, getPos, deleteNode, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id, isCorrect } = node.attrs;

    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'testQuestionChoice',
    });

    const handleToggleCorrect = useCallback(() => {
        updateAttributes({ isCorrect: !isCorrect });
    }, [isCorrect, updateAttributes]);

    const handleDelete = useCallback(() => {
        if (totalNodes <= 2) {
            return;
        }
        if (window.confirm('Are you sure you want to delete this choice?')) {
            deleteNode();
        }
    }, [deleteNode, totalNodes]);

    const handleMoveUp = useCallback(() => {
        if (nodeIndex <= 0) return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'up', { pos, node });
    }, [editor, getPos, node, nodeIndex]);

    const handleMoveDown = useCallback(() => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'down', { pos, node });
    }, [editor, getPos, node]);

    const isDeleteDisabled = totalNodes <= 2;
    const isMoveUpDisabled = nodeIndex === 0 || totalNodes <= 1;
    const isMoveDownDisabled = nodeIndex === totalNodes - 1 || totalNodes <= 1;

    return (
        <ZyNodeViewWrapper
            decorations={decorations}
            key={id}
        >
            <div
                className={`relative my-2 flex items-start gap-3 rounded-lg border-2 p-1 pr-8 transition-all ${
                    isCorrect
                        ? 'border-green-500 bg-green-50 dark:border-green-600 dark:bg-green-950/20'
                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                }`}
            >
                <div
                    className="mt-0.5 flex shrink-0 items-center gap-2"
                    contentEditable={false}
                >
                    <div className="flex flex-col items-center gap-0.5">
                        <Button
                            onClick={handleMoveUp}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-30 dark:text-gray-500 dark:hover:bg-gray-950/20 dark:hover:text-gray-400"
                            data-style="ghost"
                            aria-label="Move choice up"
                            disabled={isMoveUpDisabled || isEditorReadonly}
                            tooltip={isMoveUpDisabled ? 'This is the first choice' : 'Move choice up'}
                        >
                            <AlignTopIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-30 dark:text-gray-500 dark:hover:bg-gray-950/20 dark:hover:text-gray-400"
                            data-style="ghost"
                            aria-label="Move choice down"
                            disabled={isMoveDownDisabled || isEditorReadonly}
                            tooltip={isMoveDownDisabled ? 'This is the last choice' : 'Move choice down'}
                        >
                            <AlignBottomIcon className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                    <div className="relative">
                        <Checkbox.Root
                            checked={isCorrect}
                            disabled={isEditorReadonly}
                            onCheckedChange={handleToggleCorrect}
                            className={`flex h-5 w-5 cursor-pointer items-center justify-center rounded border-2 transition-colors ${
                                isCorrect
                                    ? 'border-green-500 bg-green-500 dark:border-green-600 dark:bg-green-600'
                                    : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                            } hover:border-gray-400 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none dark:hover:border-gray-500 dark:focus:ring-offset-gray-900`}
                            onClick={e => e.stopPropagation()}
                            title="Toggle correct answer"
                        >
                            <Checkbox.Indicator>
                                <CheckIcon className="h-3 w-3 text-white" />
                            </Checkbox.Indicator>
                        </Checkbox.Root>
                    </div>
                </div>

                <NodeViewContent className="min-w-0 w-full flex-1 self-center" />

                <div
                    className="absolute top-2 right-0"
                    contentEditable={false}
                >
                    <Button
                        onClick={handleDelete}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-950/20 dark:hover:text-red-500"
                        data-style="ghost"
                        aria-label="Delete choice"
                        disabled={isDeleteDisabled || isEditorReadonly}
                        tooltip={isDeleteDisabled ? 'A test question must have at least two choices' : 'Delete choice'}
                    >
                        <XIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </ZyNodeViewWrapper>
    );
};

export default memo(TestQuestionChoiceNodeView);
